<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter, RouterView, RouterLink } from 'vue-router'
import UserMenu from './components/UserMenu.vue'
import OnboardingTour from './components/OnboardingTour.vue'
import AnalyticsConsent from './components/AnalyticsConsent.vue'
import FrontendUpdateBanner from './components/FrontendUpdateBanner.vue'
import { useAuth } from './composables/useAuth.js'
import {
  hasCompletedFirstTour,
  hasDismissedFirstTour,
  queueFirstTour,
  startFirstTour,
} from './utils/firstTourState.js'

// The router itself is created and installed in main.js (app.use) —
// this component only consumes it.
const route = useRoute()
const router = useRouter()
const { user, refresh, updateAnalyticsConsent } = useAuth()

// /login, /auth/callback, and /showcase/* are full-page routes (no sidebar).
// /showcase/* is a DEV-only route (gated by import.meta.env.DEV in
// router.js) used for screenshot capture — it must render without the
// dashboard chrome so the captured PNGs are clean.
const isBareRoute = computed(() =>
  route.path === '/login' ||
  route.path === '/auth/callback' ||
  route.path.startsWith('/showcase/')
)

function launchTour() {
  if (!user.value?.workspace_id) return
  startFirstTour(user.value.workspace_id)
  router.push('/')
}

onMounted(async () => {
  await refresh()
  const consentParam = typeof route.query.analytics_consent === 'string'
    ? route.query.analytics_consent
    : ''
  if (
    user.value
    && user.value.cookie_consent_analytics === null
    && (consentParam === 'accepted' || consentParam === 'rejected')
  ) {
    await updateAnalyticsConsent(consentParam === 'accepted')
  }
  // First-sign-in auto-trigger: queue the tour if the user has never
  // seen it. dismissed/completed block the auto-show, but the manual
  // "Take the tour" button (above) always works.
  if (
    user.value?.workspace_id
    && !hasCompletedFirstTour(user.value.workspace_id)
    && !hasDismissedFirstTour(user.value.workspace_id)
  ) {
    queueFirstTour(user.value.workspace_id)
  }
})
</script>

<template>
  <RouterView v-if="isBareRoute" />
  <div v-else class="app">
    <aside class="sidebar">
      <div class="logo" aria-label="Prufa">
        <img class="logo-mark" src="/prufa-mark.png" alt="" width="24" height="24" />
        <span>Prufa</span>
      </div>
      <RouterLink class="sidebar-new-btn" to="/new">
        <svg class="sidebar-icon sidebar-icon--sm" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        <span>New test</span>
      </RouterLink>
      <nav>
        <RouterLink to="/" :class="{active: route.path === '/'}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1V9.5z" />
          </svg>
          <span>Workspace</span>
        </RouterLink>
        <RouterLink to="/flows" :class="{active: route.path.startsWith('/flows')}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="6" cy="6" r="2" />
            <circle cx="18" cy="6" r="2" />
            <circle cx="12" cy="18" r="2" />
            <path d="M7.5 7.5L11 16.5" />
            <path d="M16.5 7.5L13 16.5" />
          </svg>
          <span>Flows</span>
        </RouterLink>
        <RouterLink to="/monitors" :class="{active: route.path.startsWith('/monitors')}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M3 12h4l2-6 4 12 2-6h6" />
          </svg>
          <span>Monitors</span>
        </RouterLink>
        <RouterLink to="/gremlin" :class="{active: route.path.startsWith('/gremlin')}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M8 3v3M16 3v3M5 10h14l-1.5 9a2 2 0 0 1-2 1.5h-7a2 2 0 0 1-2-1.5L5 10z" />
            <path d="M9 14h.01M15 14h.01" />
          </svg>
          <span>Gremlin</span>
        </RouterLink>
        <RouterLink to="/runs" :class="{active: route.path.startsWith('/runs')}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polygon points="7 4.5 7 19.5 19 12" />
          </svg>
          <span>Runs</span>
        </RouterLink>
        <p class="nav-section">Agent surface</p>
        <RouterLink to="/api-keys" :class="{active: route.path === '/api-keys'}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="8" cy="15" r="3.5" />
            <line x1="10.5" y1="12.5" x2="20" y2="3" />
            <line x1="17" y1="6" x2="20" y2="9" />
            <line x1="14.5" y1="8.5" x2="17" y2="11" />
          </svg>
          <span>API keys</span>
        </RouterLink>
        <RouterLink to="/mcp" :class="{active: route.path === '/mcp'}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <path d="M17.5 14v7M14 17.5h7" />
          </svg>
          <span>MCP server</span>
        </RouterLink>
        <a href="/api/v1/docs" target="_blank">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="13" y2="17" />
          </svg>
          <span>API docs</span>
        </a>
        <a href="/skill/SKILL.md" target="_blank">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
          <span>Agent skill</span>
        </a>
        <p class="nav-section">Account</p>
        <RouterLink to="/billing" :class="{active: route.path === '/billing'}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <line x1="2" y1="10" x2="22" y2="10" />
          </svg>
          <span>Billing</span>
        </RouterLink>
        <RouterLink to="/support" :class="{active: route.path === '/support'}">
          <svg class="sidebar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="m4.9 4.9 4.2 4.2m5.8 5.8 4.2 4.2m0-14.2-4.2 4.2m-5.8 5.8-4.2 4.2" />
            <circle cx="12" cy="12" r="4" />
          </svg>
          <span>Support</span>
        </RouterLink>
      </nav>
      <button
        v-if="user"
        type="button"
        class="sidebar-tour-btn"
        @click="launchTour"
      >
        <svg class="sidebar-tour-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="m15.5 8.5-2.6 4.2-4.4 1 3.2 3-0.7 4.4 3.9-2.1 3.9 2.1-0.7-4.4 3.2-3-4.4-1z" />
        </svg>
        <span>Take the tour</span>
      </button>
      <UserMenu />
    </aside>
    <main class="main">
      <RouterView />
    </main>
    <FrontendUpdateBanner />
    <OnboardingTour />
    <AnalyticsConsent />
  </div>
</template>

<style scoped>
.sidebar-tour-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-top: 16px;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--ink);
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}
.sidebar-tour-btn:hover {
  background: var(--bg);
}
.sidebar-tour-icon {
  flex-shrink: 0;
  color: var(--muted);
}
.sidebar-tour-btn:hover .sidebar-tour-icon {
  color: var(--ink);
}
</style>
